import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";
import {
  createRoofType,
  CreateRoofTypeRequest,
  CreatedResponse,
} from "../../clients/BackOfficeApi";
import { useNavigate } from "react-router-dom";
import { PlusSquareIcon } from "@chakra-ui/icons";
import { isApiError, isBadRequest, ValidationErrorModel } from "../../utils/apiUtils";
import { getDynamicMessageId } from "../I18n";
import { useIntl } from "react-intl";
import { ErrorList } from "../../elements/ErrorList";
import { ConfirmCloseDirtyDialog } from "./ConfirmCloseDirtyDialog";

export function CreateRoofTypeModal() {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const [closingDirty, setClosingDirty] = useState(false);
  const [validationError, setValidationError] =
    useState<ValidationErrorModel<CreateRoofTypeRequest>>();
  const [error, setError] = useState<string>();
  const [createRoofTypeModel, setCreateRoofTypeModel] =
    useState<CreateRoofTypeRequest>();
  const navigate = useNavigate();
  const intl = useIntl();

  const isDirty = useMemo(
    () => Boolean(createRoofTypeModel?.name.trim()),
    [createRoofTypeModel?.name]
  );

  const isValid = useMemo(
    () => Boolean(createRoofTypeModel?.name.trim()),
    [createRoofTypeModel?.name]
  );

  const onCloseDirtyConfirm = useCallback((confirmed: boolean) => {
    setClosingDirty(false);
    if (!confirmed) return;

    setCreateRoofTypeModel({ name: "" });
    onClose();
  }, [onClose]);

  const onCloseGuarded = useCallback(() => {
    if (!isDirty) {
      onClose();
    }

    setClosingDirty(isDirty);
  }, [isDirty, onClose]);

  const create = useCallback(() => {
    if (!createRoofTypeModel) return;

    setValidationError(undefined);
    setError(undefined);

    createRoofType({
      body: createRoofTypeModel,
      throwOnError: true,
    })
      .then((response) => {
        if (response.response.status === 201) {
          const createdResponse = response.data as CreatedResponse;
          navigate(`/roof-types/${createdResponse.id}`);
          onClose();
        }
      })
      .catch((e) => {
        if (isApiError(e)) {
          const errorId = getDynamicMessageId(e.title ?? "Unknown error");

          setError(intl.formatMessage({ id: errorId, defaultMessage: e.title ?? "Unknown error" }));

          if (isBadRequest(e)) {
            setValidationError(e.errors);
          }
        } else {
          setError(`Fout bij het aanmaken van het daktype: ${e}`);
        }
      });
  }, [createRoofTypeModel, navigate, onClose, intl]);

  return (
    <>
      <Button onClick={onOpen}>
        <PlusSquareIcon mr={2} />
        Nieuw daktype
      </Button>
      <Modal closeOnOverlayClick isOpen={isOpen} onClose={onCloseGuarded}>
        <ModalOverlay />
        <ModalContent>
          <ConfirmCloseDirtyDialog isOpen={closingDirty} onConfirm={onCloseDirtyConfirm} />
          <ModalHeader>Nieuw daktype aanmaken</ModalHeader>
          <ModalCloseButton />
          <ModalBody as="form" onSubmit={(e) => { e.preventDefault(); create(); }}>
            {!!error && <Text color="red.500">{error}</Text>}
            <FormControl>
              <FormLabel>Naam</FormLabel>
              <Input
                isInvalid={!!validationError?.Name}
                onChange={(e) =>
                  setCreateRoofTypeModel((c) => ({
                    ...c,
                    name: e.target.value,
                  }))
                }
                placeholder="Voer de naam van het daktype in"
                value={createRoofTypeModel?.name ?? ""}
              />
            </FormControl>
            <ErrorList errors={validationError?.Name} />
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              isDisabled={!isValid}
              mr={3}
              onClick={create}
            >
              Opslaan
            </Button>
            <Button onClick={onCloseGuarded} variant="ghost">
              Annuleren
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
